import React, { useEffect, useState } from 'react';
import { HashRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import MainPage from './pages/MainPage';
import TasksPage from './pages/TasksPage';
import RoulettePage from './pages/RoulettePage';
import FriendsPage from './pages/FriendsPage';
import WalletPage from './pages/WalletPage';
import WithdrawPage from './pages/WithdrawPage';
import { giftAPI, userAPI, setAuthToken } from './services/api';
import WebApp from '@twa-dev/sdk';
import { useGameStore } from './store/gameStore';
import GiftModal from './components/GiftModal';

// Функция для парсинга initData from Telegram WebApp
const parseInitData = (initDataString: string) => {
  const result: Record<string, any> = {};
  const params = new URLSearchParams(initDataString);
  
  // Преобразуем URLSearchParams в обычный объект
  Array.from(params.entries()).forEach(([key, value]) => {
    if (key === 'user') {
      try {
        result[key] = JSON.parse(value);
      } catch (e) {
        result[key] = value;
      }
    } else {
      result[key] = value;
    }
  });
  
  // Дополнительно извлекаем параметр 'start' если он есть в URL
  // Поддерживаем новый формат ссылок: https://t.me/TarobotBot/tappyapp?start=CODE
  if (params.has('start')) {
    result.start = params.get('start');
  }
  
  return result;
};

const PROMO_BANNER_URL = process.env.REACT_APP_EXCLUSIVE_TASK_URL || 'https://t.me/your_best_present_bot/tappy';

// Компонент для обработки аутентификации
const AuthHandler: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [giftLinkId, setGiftLinkId] = useState<number | null>(null);
  const [giftTargetUrl, setGiftTargetUrl] = useState<string | null>(null);
  const [giftImageSrc, setGiftImageSrc] = useState<string | null>(null);
  const { fetchUserData } = useGameStore();
  const navigate = useNavigate();

  const openPromoLink = () => {
    try {
      const telegramWebApp = window.Telegram?.WebApp || WebApp;
      if (telegramWebApp) {
        if (PROMO_BANNER_URL.includes('t.me/') && typeof telegramWebApp.openTelegramLink === 'function') {
          telegramWebApp.openTelegramLink(PROMO_BANNER_URL);
        } else if (typeof telegramWebApp.openLink === 'function') {
          telegramWebApp.openLink(PROMO_BANNER_URL);
        } else {
          window.open(PROMO_BANNER_URL, '_blank');
        }
      } else {
        window.open(PROMO_BANNER_URL, '_blank');
      }
    } catch {
      window.open(PROMO_BANNER_URL, '_blank');
    }
  };

  useEffect(() => {
    if (!authReady) return;
    try {
      if (sessionStorage.getItem('promo_banner_dismissed') === '1') return;
    } catch {
    }

    const timer = window.setTimeout(() => {
      setPromoOpen(true);
    }, 3000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [authReady]);

  useEffect(() => {
    const handleAuth = async () => {
      try {
        setIsLoading(true);
        
        // Всегда пытаемся получить актуальные данные из Telegram WebApp
        if (WebApp) {
          
          // Получаем initData из WebApp
          const initDataString = WebApp.initData;
          
          if (!initDataString) {
            console.error('No initData available from Telegram');
            
            // Если нет initData, проверяем, есть ли сохраненный ID как запасной вариант
            const savedTelegramId = localStorage.getItem('telegram_id');
            if (savedTelegramId) {
              setAuthToken(savedTelegramId);
              await fetchUserData();
              setIsLoading(false);
              return;
            }
            
            setAuthError('Не удалось получить данные из Telegram');
            setIsLoading(false);
            return;
          }
          
          // Проверяем доступные WebApp методы и свойства
          
          // Парсим данные из строки initData
          const parsedData = parseInitData(initDataString);
          
          // Извлекаем информацию о пользователе
          let userData = parsedData.user ? 
            (typeof parsedData.user === 'string' ? JSON.parse(parsedData.user) : parsedData.user) : {};
          
          
          // Проверяем наличие обязательных полей
          if (!userData.id) {
            console.error('Missing required field: id in user data');
            
            // Альтернативный вариант - получить данные из initDataUnsafe
            if (WebApp.initDataUnsafe && WebApp.initDataUnsafe.user) {
              userData = WebApp.initDataUnsafe.user;
            } else {
              setAuthError('Не удалось получить ID пользователя Telegram');
              setIsLoading(false);
              return;
            }
          }
          
          // Формируем полный объект авторизации с гарантированными полями
          const authData = {
            user: userData,
            hash: parsedData.hash || WebApp.initDataUnsafe?.hash,
            auth_date: parsedData.auth_date || WebApp.initDataUnsafe?.auth_date,
            // Добавляем другие возможные поля из initData
            ...(parsedData.query_id && { query_id: parsedData.query_id }),
            ...(parsedData.chat_instance && { chat_instance: parsedData.chat_instance }),
            ...(parsedData.chat_type && { chat_type: parsedData.chat_type }),
            ...(parsedData.start_param && { start_param: parsedData.start_param }),
            ...(parsedData.start && { start: parsedData.start }),
            ...(parsedData.startapp && { startapp: parsedData.startapp }),
            // Для отладки - добавляем raw данные
            initDataRaw: initDataString
          };
          
          
          try {
            // Отправляем данные на сервер для аутентификации
            const authResponse = await userAPI.login(authData);
            
            // Получаем данные пользователя из ответа
            if (authResponse && authResponse.user) {
              // Сохраняем Telegram ID для последующих визитов
              const telegramId = authResponse.user.telegramId;
              setAuthToken(telegramId);
              localStorage.setItem('telegram_id', telegramId);
              
              // Загружаем данные пользователя после авторизации
              await fetchUserData();
              setAuthReady(true);

              // По умолчанию после авторизации отправляем пользователя на главный экран "Сапёр".
              // Если в start_param явно передан переход в tasks, ниже логика сделает то же самое.
              let redirectedToTasks = false;
              try {
                const startParam = (parsedData.start_param || parsedData.start || (parsedData as any).startapp || WebApp.initDataUnsafe?.start_param) as string | undefined;
                if (typeof startParam === 'string') {
                  const sp = startParam.toLowerCase();
                  if (sp === 'tasks' || sp === 'task' || sp.startsWith('tasks:') || sp.includes('action=tasks')) {
                    navigate('/tasks');
                    redirectedToTasks = true;
                  }
                }
              } catch (e) {
              }

              if (!redirectedToTasks) {
                navigate('/');
              }
            } else {
              console.error('Auth failed - no user data received');
              setAuthError('Не удалось получить данные пользователя');
            }
          } catch (error: any) {
            console.error('Auth request failed:', error);
            
            if (error.response) {
              console.error('Server response error:', {
                status: error.response.status,
                data: error.response.data
              });
              
              if (error.response.data && error.response.data.errors) {
                const errorMessages = error.response.data.errors.map((err: any) => err.msg).join(', ');
                setAuthError(`Ошибка аутентификации: ${errorMessages}`);
              } else if (error.response.data && error.response.data.error) {
                setAuthError(`Ошибка аутентификации: ${error.response.data.error}`);
              } else {
                setAuthError(`Ошибка аутентификации: ${error.response.status}`);
              }
            } else {
              setAuthError('Не удалось подключиться к серверу аутентификации');
            }
            
            throw error;
          }
        } else {
          setAuthError('Приложение должно запускаться в Telegram');
        }
      } catch (error) {
        console.error('Authentication error:', error);
        setAuthError('Ошибка аутентификации');
      } finally {
        setIsLoading(false);
      }
    };
    
    handleAuth();
  }, []);

  useEffect(() => {
    if (!authReady) return;

    const giftImages = (() => {
      try {
        const ctx = (require as any).context('./assets/gifts', false, /\.(png|jpe?g|gif|svg|webp)$/i);
        return ctx.keys().map((k: string) => {
          const result = ctx(k);
          return result?.default || result;
        });
      } catch {
        return [] as string[];
      }
    })();

    const timer = setTimeout(async () => {
      try {
        const resp = await giftAPI.getAvailable();
        const directUrl = resp?.link?.url || resp?.link?.trackingUrl;
        if (!resp || !resp.success || !resp.available || !directUrl) return;

        const image = giftImages.length
          ? giftImages[Math.floor(Math.random() * giftImages.length)]
          : null;

        setGiftLinkId(resp.link?.id ?? null);
        setGiftTargetUrl(directUrl);
        setGiftImageSrc(image);
        setGiftOpen(true);
      } catch (e) {
      }
    }, 30_000);

    return () => clearTimeout(timer);
  }, [authReady]);

  const normalizeGiftLink = (rawLink: string): string => {
    if (!rawLink) return '';
    let url = rawLink.trim();

    if (!url.startsWith('http://') && !url.startsWith('https://') && url.startsWith('t.me/')) {
      url = `https://${url}`;
    }

    if (url.startsWith('tg://resolve?')) {
      try {
        const parsed = new URL(url);
        const domain = parsed.searchParams.get('domain');
        if (domain) {
          url = `https://t.me/${domain}`;
        }
      } catch {
      }
    }

    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('tg://')) {
      if (url.includes('.')) {
        url = `https://${url}`;
      }
    }

    return url;
  };

  const openGift = () => {
    if (!giftTargetUrl) {
      setGiftOpen(false);
      return;
    }

    const url = normalizeGiftLink(giftTargetUrl);
    if (!url) {
      setGiftOpen(false);
      return;
    }

    if (giftLinkId) {
      giftAPI.trackClick(giftLinkId).catch(() => {});
    }
    const telegramWebApp = window.Telegram?.WebApp || WebApp;
    if (telegramWebApp) {
      if (url.includes('t.me/') && typeof telegramWebApp.openTelegramLink === 'function') {
        telegramWebApp.openTelegramLink(url);
      } else if (typeof telegramWebApp.openLink === 'function') {
        telegramWebApp.openLink(url);
      } else {
        window.open(url, '_blank');
      }
    } else {
      window.open(url, '_blank');
    }

    setGiftOpen(false);
  };
  
  if (isLoading) {
    return (
      <div className="auth-loading">
        <div className="auth-loading-hero">
          <div className="auth-loading-text">Загружаем ваши подарки...</div>
          <div className="auth-loading-bar">
            <div className="auth-loading-bar-fill" />
          </div>
        </div>
      </div>
    );
  }
  
  if (authError) {
    return (
      <div className="auth-error">
        <p>{authError}</p>
        <button onClick={() => window.location.reload()}>Попробовать снова</button>
      </div>
    );
  }
  
  return (
    <>
      {children}
      {promoOpen && (
        <div
          className="bottom-promo"
          role="button"
          tabIndex={0}
          onClick={() => {
            try {
              sessionStorage.setItem('promo_banner_dismissed', '1');
            } catch {
            }
            setPromoOpen(false);
            openPromoLink();
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            try {
              sessionStorage.setItem('promo_banner_dismissed', '1');
            } catch {
            }
            setPromoOpen(false);
            openPromoLink();
          }}
        >
          <button
            className="bottom-promo-close"
            type="button"
            aria-label="Закрыть"
            onClick={(e) => {
              e.stopPropagation();
              try {
                sessionStorage.setItem('promo_banner_dismissed', '1');
              } catch {
              }
              setPromoOpen(false);
            }}
          >
            ×
          </button>
          <div className="bottom-promo-content">
            <div className="bottom-promo-title">✨ Получи 1000 звёзд Telegram!</div>
            <div className="bottom-promo-subtitle">Нажми, чтобы узнать как!</div>
          </div>
          <img className="bottom-promo-image" src="/img/Plush%20Pepe.webp" alt="Plush Pepe" />
        </div>
      )}
      <GiftModal
        isOpen={giftOpen}
        imageSrc={giftImageSrc}
        onClose={() => setGiftOpen(false)}
        onClaim={openGift}
      />
    </>
  );
};

// Добавляем немного стилей для индикатора загрузки
const AppWithStyles: React.FC = () => {
  return (
    <>
      <style>
        {`
          .auth-loading, .auth-error {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100vh;
            position: relative;
            font-family: 'Trebuchet MS', 'Segoe UI', sans-serif;
            background: url('/img/pepeDown.png') no-repeat center center / cover;
          }
          .auth-error {
            color: #ff4d4f;
          }
          .auth-error button {
            margin-top: 16px;
            padding: 8px 16px;
            background-color: #1890ff;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
          }
          
          .auth-loading-hero {
            position: absolute;
            bottom: 48px;
            left: 0;
            right: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            gap: 16px;
            padding: 0 24px;
            max-width: none;
            min-height: auto;
            background: transparent;
            border-radius: 0;
            box-shadow: none;
          }

          .auth-loading-text {
            font-size: 22px;
            font-weight: 700;
            color: #1b2d45;
            text-shadow: 0 2px 0 rgba(255, 255, 255, 0.7);
          }

          .auth-loading-bar {
            width: min(80vw, 320px);
            height: 10px;
            border-radius: 999px;
            background: rgba(70, 140, 200, 0.2);
            border: 1px solid rgba(60, 130, 190, 0.35);
            box-shadow: inset 0 0 6px rgba(255, 255, 255, 0.7);
            overflow: hidden;
          }

          .auth-loading-bar-fill {
            height: 100%;
            width: 40%;
            border-radius: 999px;
            background: linear-gradient(90deg, #5fb6ff, #3c8fd9, #5fb6ff);
            animation: auth-loading-bar 1.8s ease-in-out infinite;
          }

          @keyframes auth-loading-bar {
            0% {
              transform: translateX(-60%);
            }
            50% {
              transform: translateX(40%);
            }
            100% {
              transform: translateX(120%);
            }
          }
        `}
      </style>
      
    <HashRouter>
        <AuthHandler>
      <Routes>
        <Route path="/" element={<MainPage />} />
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/tap" element={<MainPage />} />
        <Route path="/roulette" element={<RoulettePage />} />
        <Route path="/friends" element={<FriendsPage />} />
        <Route path="/wallet" element={<WalletPage />} />
        <Route path="/withdraw" element={<WithdrawPage />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
        </AuthHandler>
    </HashRouter>
    </>
  );
};

export default AppWithStyles;
